/**
 * Helpers for Customer Documents factory methods
 */

import type { IBaseCustomer } from "../../BaseCustomerFactory";
import type { Document } from "le-coffre-resources/dist/Customer";
import type { ApiPayload, QueryPayload } from "../../../../types";
import { prepareGetByUidQueryForRequest } from "@Front/Api/helpers/getByUidClientOptionsHelpers";
import type { IGetDocumentsparams, IPutDocumentsParams } from "../Documents";
import {
	buildDocumentsCreatePayload,
	buildDocumentsUpdatePayload,
	buildEmptyPayload,
} from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

/**
 * Create GET method for documents
 */
export function createGetMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (q: IGetDocumentsparams): Promise<Document[]> => {
		const url = new URL(baseUrl);
		const query = { q };
		if (q) baseCustomer.appendJsonSearchParams(url, query);
		try {
			return await baseCustomer.getRequest<Document[]>(url);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for documents
 */
export function createPostMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (body: ApiPayload): Promise<Document> => {
		const url = new URL(baseUrl);
		try {
			const payload = buildDocumentsCreatePayload(body);
			return await baseCustomer.postRequest<Document>(url, payload);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create GET by UID method for documents
 */
export function createGetByUidMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const { requestQuery, suppressUserFacingErrorOnFailure } = prepareGetByUidQueryForRequest(q);
		if (requestQuery) baseCustomer.appendJsonSearchParams(url, { q: requestQuery });
		try {
			return await baseCustomer.getRequest<Document>(url);
		} catch (err) {
			if (!suppressUserFacingErrorOnFailure) {
				baseCustomer.onError(err);
			}
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method
 */
export function createPutMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string, body: IPutDocumentsParams): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			const payload = buildDocumentsUpdatePayload(body);
			return await baseCustomer.putRequest<Document>(url, payload);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create DELETE method
 */
export function createDeleteMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			return await baseCustomer.deleteRequest<Document>(url);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create mark as viewed method
 */
export function createMarkAsViewedMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string): Promise<Document> => {
		const url = new URL(baseUrl.concat(`/${uid}/mark-as-viewed`));
		try {
			return await baseCustomer.putRequest<Document>(url, buildEmptyPayload());
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}
